import { Alert, Box, Button, Flex, Image } from '@chakra-ui/react';
import { MdCloudUpload, MdWarning } from 'react-icons/md';
import { useCallback, useState } from 'react';

import { Txt } from '@app/components/common';
import { Upload } from '@app/components/common/upload';
import { useAlert, useApi } from '@app/hooks';

import { TemplateDownload } from '../components/template-download';
import { Confirm } from '../components/confirm';
import { ExampleTableStyle } from '../constants';

export const ConditionsImport = () => {
  const api = useApi();
  const { error, success } = useAlert();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState<File>();

  const onFileChange = useCallback((_: string, file: File) => {
    setFile(file);
  }, []);

  const onImportClick = useCallback(() => {
    if (!file) {
      error('Файл не выбран');
      return;
    }
    setOpen(true);
  }, [error, file]);

  const onImport = useCallback(async () => {
    setLoading(true);
    const content: string = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.readAsDataURL(file);
    });

    const response = await api.imports.uploadConditions({
      filename: file.name,
      content,
    });
    if (response.code === 200) {
      success(response.message);
    } else {
      error(response.message);
    }
    setLoading(false);

  }, [api.imports, error, file, success]);

  const onConfirm = useCallback(() => {
    setOpen(false);
    onImport();
  }, [onImport]);

  const onClose = useCallback(() => {
    setOpen(false);
  }, []);

  return <>
    <Flex flexDirection='column' gap={4} p={{ base: 2, md: 0 }}>
      <Flex gap={4} alignItems='flex-start' justifyContent='space-evenly' flexDirection={{ base: 'column', md: 'row' }}>
         <Box flex={1} >
          <Alert status='info' flexDirection='column' alignItems='flex-start' minH='100%'>
            <Box>
              <Txt>Импорт технического состояния холодильного оборудования.</Txt> 
            </Box>
            <Box mt={2}>
              <Txt>Файл должен содержать в себе колонки:</Txt> 
            </Box>
            <Box ml={4}>
              <ul>
                <li><Txt color='gray.500'>Инвентарный номер оборудования</Txt></li>
                <li><Txt color='gray.500'>Статус оборудования (наименование статуса из справочника системы <b>SNEG</b>)</Txt></li>
                <li><Txt color='gray.500'>Техническое состояние (наименование состояния из справочника системы <b>SNEG</b>)</Txt></li>
              </ul>
            </Box>
          </Alert>
          <Box mt={2}>
            <TemplateDownload label="Получить шаблон" method={api.imports.getConditionsTemplate} />
          </Box>
        </Box>
        <Flex justifyContent='center' alignItems='center' minW={{ base: 0, lg: 429}} mt={0}>
          <Box __css={ExampleTableStyle}>
            <table>
              <tr>
                <th align='center'>Инвентарный номер</th>
                <th align='center'>Статус</th>
                <th align='center'>Техническое состояние</th>
              </tr>
              <tr>
                <td align='right'>ЦБ-012345</td>
                <td align='right'>В эксплуатации</td>
                <td align='right'>Рабочий</td>
              </tr>
              {Array.from({ length: 4 }).map((_, i) => <tr key={i}><td align='right'>&nbsp;</td><td align='right'></td><td align='right'></td></tr> )}
            </table>
          </Box>
        </Flex>
      </Flex>
      <Box minH={200} display='flex' flexDirection='column'>
        <Upload onChange={onFileChange} label="Выберите Excel файл с QR-кодами ХО" accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" />
      </Box>
      <Flex gap={4} alignItems='flex-start'>
        <Alert status='warning' display='flex' alignItems='flex-start' >
          <Box mt={2} mr={3}>
            <MdWarning size={24} />
          </Box>
          Пожалуйста, обратите внимание, что процесс импорта данных происходит автоматически. Убедитесь, что данные, которые вы импортируете, действительно содержат необходимые изменения, так как после импорта их будет невозможно откатить и вернуть в исходное состояние.
        </Alert>
        <Box>
          <Button isLoading={loading} onClick={onImportClick} variant='solid' colorScheme='brand' size='md' iconSpacing={2} leftIcon={<MdCloudUpload size='24px' />} aria-label="добавить">Загрузить</Button>
        </Box>
      </Flex>
    </Flex>
    <Confirm open={open} onClose={onClose} onConfirm={onConfirm} />
  </>;
};